import React, { useEffect, useState, useRef } from 'react';
import DataGridContainer from '../datagrid/dataGridContainer/DataGridContainer';
import { DO_NOTHING, viewMap } from '../../utils/commonfunctions';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import CustomFooter from '../datagrid/paginationFooter/CustomFooter';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { updateUserPreferences } from '../../utils/api/userPreference_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { updateColumnVisibility } from '../../features/userPreference/userPreferenceSlice';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Chip } from '@mui/material';
import { breakpoints } from '../../theme/theme';

const DEFAULT_TABLE_TITLE = 'Table Title';
const DEFAULT_ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const EMPTY_HEADER_ACTION_BUTTONS = [];

export default function TableComponent({
    title = DEFAULT_TABLE_TITLE,
    headerActionButtons = EMPTY_HEADER_ACTION_BUTTONS,
    headerContent = null,
    headerContentRight = null,
    isCheckbox = false,
    idField = 'id',
    loading = false,
    isColumnResizable = false,
    isRowSelection = false,
    selectedRows = [],
    handleRowSelection = DO_NOTHING,
    isPagination = false,
    paginationMode = 'client',
    rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
    rowCount = 0,
    paginationModel = { page: 0, pageSize: 25 },
    handlePaginationModelChange = DO_NOTHING,
    filterMode = 'client',
    filterModel = null,
    handleFilterModelChange = DO_NOTHING,
    sortingMode = 'client',
    sortModel = null,  // Controlled sort model for DataGrid
    handleSortModelChange = DO_NOTHING,
    rows = [],
    columns = [],
    components = null,
    initialState = null,
    isColumnVisibilityModelChange = false,
    handleColumnVisibilityModelChange = DO_NOTHING,
    handleCustomPaginationPageChange = DO_NOTHING,
    handleCustomPaginationPageSizeChange = DO_NOTHING,
    isCustomPagination = false,
    handleRowClick = null,
    disableColumnMenu = false,
    isRowSelectable = null,
    getRowClassName = null,
    viewType = 'testRun',
    disableSelectionOnClick = false,
    getRowHeight = null,
    getEstimatedRowHeight = null,
    customStyles = {},
    isData = true,
    customNoData = null,
    isVisibleInsideTableNoData = false,
    tableHeight = "100%",
    autoHeight = false,
    disablePagination = false,
    disableFooter = false,
    hideColumns = null,
    hideColumnsVisibilityButton = false,
    isHeaderContentVisible=true,
    apiRef: externalApiRef = null,
    showTitleTooltip = false,
    rowReordering = false,
    onRowOrderChange = null,
}) {
    const dispatch = useDispatch();
    const internalApiRef = useGridApiRef();
    // Use external apiRef if provided, otherwise use internal one
    const apiRef = externalApiRef || internalApiRef;
    const columnVisibilityRef = useRef(null);
    const userPreference = useSelector((state) => state.userPreference || {});
    const columnVisibilityModel = userPreference?.[viewMap[viewType]]?.columnVisible || {};

    const [baselineColumnVisibility] = useState(() => columnVisibilityModel);


    const handleOpenColumnsPanel = () => {
        // Added null check for v8 compatibility
        if (apiRef.current) {
            apiRef.current.showPreferences('columns');
        }
    };

    const [customHeaderActionButtons, setCustomHeaderActionButtons] = useState(headerActionButtons);
    const [density, setDensity] = useState('comfortable');

    // Responsive density based on screen size
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= breakpoints.xl) {
                setDensity('standard');
            } else {
                setDensity('compact');
            }
        };

        // Check on mount
        handleResize();

        // Add event listener
        window.addEventListener('resize', handleResize);

        // Cleanup
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if(hideColumnsVisibilityButton) {
            setCustomHeaderActionButtons(headerActionButtons);
            return;
        } else {
            const columnsButton = {
                name: "Columns",
                tooltip: "Show/Hide Columns",
                icon: <Bars3Icon className='icon' />,
                action: handleOpenColumnsPanel,
                disabled: false,
                section: 'right',
                contained: false,
            };
            const filtered = headerActionButtons.filter(btn => btn.name !== "Columns");
            setCustomHeaderActionButtons([columnsButton, ...filtered]);
        }
    }, [headerActionButtons]);

    useEffect(() => {
        const saveColumnVisibility = () => {
            if (columnVisibilityRef.current) {
                dispatch(updateColumnVisibility(columnVisibilityRef.current));
            }
        };

        window.addEventListener("beforeunload", saveColumnVisibility);

        return () => {
            saveColumnVisibility(); 
            window.removeEventListener("beforeunload", saveColumnVisibility);
        };
    }, [dispatch]);


    //     const handleColumnVisibilityModelChangeCustom = async (newModel, tdata) => {
    //     const allColumns = tdata.api.getAllColumns()?.map(column => column.field);
    //     const visibleColumns = tdata.api.getVisibleColumns()?.map(column => column.field);
    //     let tColumnVisibilityModel = {};
    //     allColumns.forEach(column => {
    //         tColumnVisibilityModel[column] = visibleColumns.includes(column);
    //     });
    //     dispatch(updateColumnVisibility({page: viewType, columnVisible: tColumnVisibilityModel}));
    //     const payload = [{
    //         viewType: viewType,
    //         preferences: {columnVisible: visibleColumns,
    //         }
    //     }]
    //     await updateUserPreferences(payload);
    // }

    const handleColumnVisibilityModelChangeCustom = async (newModel, tdata) => {

        columnVisibilityRef.current = { page: viewType, columnVisible: newModel };

        const visibleColumns = Object.keys(newModel).filter(col => newModel[col]);
        const payload = [{
            viewType,
            preferences: { columnVisible: visibleColumns }
        }];

        const response = await updateUserPreferences(payload);
        
        if (response.status === STATUS.ERROR) {
            console.error('Failed to update user preferences:', response.error);
        }
    }

    // Helper function to convert old selection model to new v8 format
    const convertSelectionModelToV8 = (selectionModel) => {
        if (Array.isArray(selectionModel)) {
            return {
                type: 'include',
                ids: new Set(selectionModel)
            };
        }
        return selectionModel;
    };

    // Process columns to add disableReorder to Actions column
    const processedColumns = (hideColumns ? columns.map(col => hideColumns.includes(col.field) ? { ...col, hideable: false } : col) : columns) || [];
    const columnsWithActionsDisabled = processedColumns.map(col => 
        (col.field === 'Actions' || col.field === 'actions') ? { ...col, disableReorder: true } : col
    );

    const dataGridProps = {
        density: 'compact',
        rows: rows || [],
        columns: columnsWithActionsDisabled,
        getRowId: (row) => row?.[idField] ?? Math.random(),
        checkboxSelection: isCheckbox,
        loading,
        disableColumnResize: !isColumnResizable,
        disableRowSelectionOnClick: true,
        pagination: !disablePagination,
        hideFooterPagination: disableFooter,
        hideFooter: disableFooter,
        disableColumnSelector: true,
        disableColumnFilter: true,  
        sx: {
            ...customStyles,
            "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-iconSeparator": { display: "none" },
            "& .MuiDataGrid-columnHeaderCheckbox": { cursor: "default" },
            ...(handleRowClick && { "& .MuiDataGrid-row": { cursor: "pointer" } }),
            "& .MuiDataGrid-columnHeader--sortable:hover": { cursor: "pointer" },
            "& .MuiDataGrid-menuIconButton:hover": { cursor: "pointer" },
            "& .MuiDataGrid-checkboxInput": { cursor: "pointer" },
            "& .MuiTablePagination-toolbar button": { cursor: "pointer" },
            "& .MuiTablePagination-select": { cursor: "pointer" },
            "& .MuiDataGrid-filterForm button": { cursor: "pointer" },
            "& .MuiDataGrid-filterForm select": { cursor: "pointer" },
            "& .MuiDataGrid-columnsPanel input[type='checkbox']": { cursor: "pointer" },
            "& .MuiFormControlLabel": { cursor: "pointer" }
        },
        disableSelectionOnClick,
        apiRef,
    };

    if (isRowSelection) {
        // Convert array format to v8 object format for internal use
        const v8SelectionModel = convertSelectionModelToV8(selectedRows);
        
        dataGridProps.onRowSelectionModelChange = (newSelectionModel) => {
            // Convert back to array format for backward compatibility with existing handlers

        const tselectedRow = (()=>{
            if(newSelectionModel.type === 'include'){
                return Array.from(newSelectionModel.ids || []);
            } else if(newSelectionModel.type === 'exclude'){
                return rows.map((item)=>item[idField])           
            }
            return newSelectionModel;
        })()

            if (typeof handleRowSelection === 'function') {
                handleRowSelection(tselectedRow);
            }
        };
        dataGridProps.rowSelectionModel = v8SelectionModel;

        // Retain selections across pages when rows are server-paginated (MUI drops
        // IDs not in the current rows prop unless this is enabled).
        if (isPagination && paginationMode === 'server') {
            dataGridProps.keepNonExistentRowsSelected = true;
        }
    }

    if (isPagination) {
        dataGridProps.paginationMode = paginationMode;
        dataGridProps.rowsPerPageOptions = rowsPerPageOptions;
        dataGridProps.rowCount = rowCount;
        dataGridProps.paginationModel = paginationModel;
        dataGridProps.onPaginationModelChange = handlePaginationModelChange;
    }

    if (filterMode === 'server' || filterMode === 'client') {
        dataGridProps.filterMode = filterMode;
        dataGridProps.onFilterModelChange = handleFilterModelChange;
        if (filterModel) dataGridProps.filterModel = filterModel;
    }

    if (sortingMode === 'server' || sortingMode === 'client') {
        dataGridProps.sortingMode = sortingMode;
        dataGridProps.onSortModelChange = handleSortModelChange;
        // Pass sortModel for controlled sorting (shows sort indicators in UI)
        if (sortModel) {
            dataGridProps.sortModel = sortModel;
        }
    }

    // Use slots instead of components (components is deprecated in v8)
    if (components) {
        dataGridProps.slots = components;
    }

    const shouldRenderCustomNoDataOverlay = Boolean(customNoData) && isVisibleInsideTableNoData;
    if (shouldRenderCustomNoDataOverlay) {
        dataGridProps.slots = {
            ...dataGridProps.slots,
            noRowsOverlay: () => customNoData,
            noResultsOverlay: () => customNoData,
        };
    }

    if (initialState) {
        dataGridProps.initialState = {
            ...initialState,
            columns: {
                ...initialState?.columns,
                columnVisibilityModel: baselineColumnVisibility /* columnVisibilityModel */,
            },
        };
    } else {
        dataGridProps.initialState = {
            columns: { columnVisibilityModel: baselineColumnVisibility /* columnVisibilityModel */ },
        };
        dataGridProps.onColumnVisibilityModelChange = handleColumnVisibilityModelChangeCustom;
    }

    if (isColumnVisibilityModelChange) {
        dataGridProps.onColumnVisibilityModelChange = handleColumnVisibilityModelChange;
    }

    if (isCustomPagination) {
        let attributes = {};
        if (paginationMode === 'server') {
            attributes = {
                isServerMode: true,
                page: paginationModel.page,
                pageSize: paginationModel.pageSize,
                rowCount: rowCount,
                onPageChange: handleCustomPaginationPageChange,
                onPageSizeChange: handleCustomPaginationPageSizeChange,
            };
        }
        dataGridProps.slots = {
            ...dataGridProps.slots,
            footer: () => <CustomFooter {...attributes} />,
        };
    }

    if (handleRowClick) dataGridProps.onRowClick = handleRowClick;
    if (disableColumnMenu) dataGridProps.disableColumnMenu = disableColumnMenu;
    if (isRowSelectable) dataGridProps.isRowSelectable = isRowSelectable;
    if (getRowClassName) dataGridProps.getRowClassName = getRowClassName;
    if (getRowHeight) dataGridProps.getRowHeight = getRowHeight;
    if (getEstimatedRowHeight) dataGridProps.getEstimatedRowHeight = getEstimatedRowHeight;
    if (autoHeight) dataGridProps.autoHeight = autoHeight;
    if (rowReordering) {
        dataGridProps.rowReordering = true;
        if (onRowOrderChange) dataGridProps.onRowOrderChange = onRowOrderChange;
    }

    return (
      <DataGridContainer
        name={title}
        showTitleTooltip={showTitleTooltip}
        headerActionButtons={customHeaderActionButtons}
        isHeaderContentVisible={isHeaderContentVisible}
        headerContent={() => (
          <Chip
            variant="dataGridChip"
            label={`${rowCount || rows.length} total`}
          />
        )}
        headerContentRight={headerContentRight}
      >
        <Box sx={{ height: autoHeight ? "auto" : tableHeight, width: "100%" }}>
          {isData ? (
            <DataGridPro
              {...dataGridProps}
              density={density}
              sx={{              
                ...(dataGridProps.sx || {}),
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                "& .MuiDataGrid-iconSeparator": {
                  opacity: 0.7,
                  color: "#474444",
                  backgroundColor: "transparent",
                  transition: "color 0.2s, opacity 0.2s",
                },
                "& .MuiDataGrid-columnHeader--last .MuiDataGrid-columnSeparator": {
                  display: "none !important",
                },
                "& .MuiDataGrid-root": {
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                },
                "& .MuiDataGrid-main": {
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                },
                "& .MuiDataGrid-footerContainer": {
                  flexShrink: 0,
                },
              }}
            />
          ) : (
            customNoData
          )}
        </Box>
      </DataGridContainer>
    );
}